class DrawerSearchSection extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.drawerClass = "wt-drawer-search";
    this.classDrawerActive = `${this.drawerClass}--active`;
    this.pageOverlayClass = "search-overlay";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;

    this.triggerQuery = [
      ".wt-header__search-trigger",
      ".wt-header__search__close",
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);

    this.cachedResults = {};
    this.input = this.querySelector('input[name="q"]');
    this.clearButton = this.querySelector(".wt-header__search__clear-button");
    this.closeButton = this.querySelector(".wt-header__search__close");
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.mainTrigger = this.querySelector(".wt-header__search-trigger");
    this.emptyAnnouncement = this.querySelector(".search-empty");
    this.resultsPanel = this.querySelector(".wt-header__search__results");

    this.isVisibleClearButton = false;

    this.saveSuggestionMenuInDesignMode =
      this.saveSuggestionMenuInDesignMode.bind(this);
    this.setupEventListeners();

    this.toggleTabindexElements = [this.input, this.closeButton];
    this.mobileMq = window.matchMedia("(max-width: 989px)");
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onResize = () => {
      if (this.isOpen) this.syncMobileSearchLayout();
    };

    this.init();
  }

  connectedCallback() {
    document.addEventListener("click", this.onDocumentClick);
    window.addEventListener("resize", this.onResize);
    this.restoreMobileSearchLayout();
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onDocumentClick);
    window.removeEventListener("resize", this.onResize);
  }

  onDocumentClick(e) {
    const externalTrigger = e.target.closest('[data-action="search"]');
    if (externalTrigger && !externalTrigger.closest("search-drawer")) {
      e.preventDefault();
      e.stopPropagation();
      if (!this.isOpen) {
        this.openSearch();
      }
      return;
    }

    if (!this.isOpen) return;
    if (this.contains(e.target)) return;

    this.closeSearch();
  }

  restoreMobileSearchLayout() {
    const close = this.querySelector(".wt-header__search__close");
    const form = this.querySelector(".wt-header__search__form");
    if (close && form && !form.contains(close)) {
      form.appendChild(close);
    }
    this.querySelector(".mobile-search-overlay__toolbar")?.remove();
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input:not([type='hidden']), select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  openSearch() {
    if (this.isOpen) return;

    this.setAttribute("open", "");
    setTabindex(this.toggleTabindexElements, "0");
    setTabindex([this.mainTrigger], "-1");
    this.isOpen = true;

    this.classList.add(this.classDrawerActive);
    this.body.classList.add(this.activeOverlayBodyClass);

    if (!this.getQuery()) {
      this.removeAttribute("results");
      this.removeAttribute("loading");
      this.removeAttribute("empty");
    }

    this.syncMobileSearchLayout();

    requestAnimationFrame(() => {
      this.input?.focus({ preventScroll: true });
      this.syncMobileSearchLayout();
    });
  }

  closeSearch() {
    if (!this.isOpen) return;

    this.removeAttribute("open");
    setTabindex(this.toggleTabindexElements, "-1");
    setTabindex([this.mainTrigger], "0");
    this.isOpen = false;

    this.classList.remove(this.classDrawerActive);
    this.body.classList.remove(this.activeOverlayBodyClass);
    this.removeAttribute("data-search-solo-bar");
    this.clearMobileSearchLayout();

    setTimeout(() => {
      this.mainTrigger?.focus({ preventScroll: true });
    }, 0);
  }

  syncMobileSearchLayout() {
    if (!this.mobileMq.matches || !this.isOpen) return;

    const form = this.querySelector(".wt-header__search__form");
    const results = this.resultsPanel;
    if (!form || !results) return;

    const announcementBar = document.querySelector(
      ".wt-announcement-bar, announcement-bar.wt-announcement-bar",
    );
    let announcementOffset = 0;

    if (announcementBar) {
      const styles = window.getComputedStyle(announcementBar);
      const rect = announcementBar.getBoundingClientRect();
      if (
        styles.display !== "none" &&
        styles.visibility !== "hidden" &&
        rect.height > 0 &&
        rect.bottom > 0 &&
        rect.top < 120
      ) {
        announcementOffset = Math.ceil(rect.height);
      }
    }

    document.documentElement.style.setProperty(
      "--mobile-search-announcement-offset",
      `${announcementOffset}px`,
    );

    const resultsVisible =
      results.offsetHeight > 0 &&
      !results.matches("[hidden]") &&
      (this.hasAttribute("results") ||
        this.hasAttribute("loading") ||
        results.querySelector(".predictive-search-status:not(:empty)"));

    const isSoloBar = !resultsVisible;
    this.toggleAttribute("data-search-solo-bar", isSoloBar);
  }

  clearMobileSearchLayout() {
    document.documentElement.style.removeProperty(
      "--mobile-search-announcement-offset",
    );
  }

  toggleDrawerClasses() {
    if (this.isOpen) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  init() {
    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        if (trigger.classList.contains("wt-header__search__close")) {
          this.closeSearch();
        } else {
          this.toggleDrawerClasses();
        }
      });
    });

    this.clearButton?.addEventListener("click", () => {
      this.input.value = "";
      this.clearButton.style.display = "none";
      this.isVisibleClearButton = false;
      this.clearResults();
    });

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.closeSearch();
        }
      }

      if (isTabPressed && this.isOpen) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.input?.addEventListener("input", () => {
      if (this.input.value.length > 0 && !this.isVisibleClearButton) {
        this.clearButton.style.display = "flex";
        this.clearButton.setAttribute("tabindex", "0");
        this.isVisibleClearButton = true;
      } else if (this.input.value.length === 0 && this.isVisibleClearButton) {
        this.clearButton.style.display = "none";
        this.clearButton.setAttribute("tabindex", "-1");
        this.isVisibleClearButton = false;
      }
    });

    this.predictiveSearchResults?.addEventListener("click", (e) => {
      const viewAll = e.target.closest(".search-result-more__link");
      if (!viewAll) return;
      e.preventDefault();
      const term = this.getQuery();
      if (!term.length) return;
      window.location.href = `${window.routes?.search_url || "/search"}?q=${encodeURIComponent(term)}&type=product`;
    });
  }

  setupEventListeners() {
    const form = this.querySelector("form.store-search-form");
    form?.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input?.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (Shopify.designMode) {
      document.addEventListener(
        "shopify:section:load",
        this.saveSuggestionMenuInDesignMode,
      );
    }
  }

  saveSuggestionMenuInDesignMode() {
    if (this.body.classList.contains(this.activeOverlayBodyClass)) {
      this.classList.toggle(this.classDrawerActive);
    }
  }

  getQuery() {
    return this.input?.value.trim() || "";
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (searchTerm.length) {
      this.getSearchResults(searchTerm);
      this.removeAttribute("empty");
    } else {
      this.clearResults();
    }
  }

  onFormSubmit(event) {
    event.preventDefault();

    const term = this.getQuery();
    if (!term.length) return;

    const selectedLink = this.querySelector('[aria-selected="true"] a');
    if (selectedLink?.href) {
      window.location.href = selectedLink.href;
      return;
    }

    this.getSearchResults(term);
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent("resources[type]")}=product,page,article,collection,query&${encodeURIComponent("resources[limit_scope]")}=each&${encodeURIComponent("resources[limit]")}=6&${encodeURIComponent("resources[options][fields]")}=title,variants.sku,product_type&section_id=predictive-search`,
    )
      .then((response) => {
        if (!response.ok) {
          let error = new Error(response.status);
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
    this.syncMobileSearchLayout();
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.syncMobileSearchLayout();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        ?.textContent,
    );
    this.syncMobileSearchLayout();
  }

  clearResults() {
    if (this.input) this.input.value = "";
    this.removeAttribute("results");
    this.removeAttribute("loading");
    this.removeAttribute("empty");
    this.syncMobileSearchLayout();
  }
}

customElements.define("search-drawer", DrawerSearchSection);
